import { useEffect, useState } from "react";
import { api, formatApiError } from "../lib/api";
import { useAuth } from "../context/AuthContext";
import { Navigate } from "react-router-dom";
import { toast } from "sonner";
import { Users, BookOpen, MessageSquare, Mail, Euro, BadgeCheck, Copy, Download, KeyRound, ShieldCheck, Code, TestTube2, Database, HardDrive, Activity, Sparkles, UserPlus, Star, XCircle, Trash2 } from "lucide-react";

export default function AdminPage() {
  const { user, loading } = useAuth();
  const [stats, setStats] = useState(null);
  const [signups, setSignups] = useState([]);
  const [mailing, setMailing] = useState([]);
  const [resets, setResets] = useState([]);
  const [verifications, setVerifications] = useState([]);
  const [activity, setActivity] = useState([]);
  const [error, setError] = useState("");
  const [refreshing, setRefreshing] = useState(false);
  const [lastRefreshedAt, setLastRefreshedAt] = useState(null);

  const load = async () => {
    setRefreshing(true);
    try {
      const [s, sg, ml, rs, ve, ac] = await Promise.all([
        api.get("/admin/stats"),
        api.get("/admin/signups"),
        api.get("/admin/mailing-list"),
        api.get("/admin/password-resets"),
        api.get("/admin/email-verifications"),
        api.get("/admin/activity"),
      ]);
      setStats(s.data);
      setSignups(sg.data);
      setMailing(ml.data);
      setResets(rs.data);
      setVerifications(ve.data);
      setActivity(ac.data);
      setLastRefreshedAt(new Date());
      setError("");
    } catch (e) {
      setError(formatApiError(e));
    } finally {
      setRefreshing(false);
    }
  };

  // Manually flip Reads+ on/off for any user, from the members table.
  const togglePlus = async (u) => {
    const isPlusNow = u.subscription_status === "plus" || Boolean(u.is_plus);
    const verb = isPlusNow ? "Revoke Reads+ from" : "Grant Reads+ to";
    if (!window.confirm(`${verb} ${u.email}?`)) return;
    try {
      const { data } = await api.patch(`/admin/users/${u.id}/plus`, { is_plus: !isPlusNow, days: 30 });
      setSignups((rows) => rows.map((r) => (r.id === u.id ? data : r)));
      toast.success(isPlusNow ? "Reads+ revoked" : "Reads+ granted (30 days)");
    } catch (e) {
      toast.error(formatApiError(e));
    }
  };

  // Nuke a user + all their data. Cascades on the backend.
  const deleteUser = async (u) => {
    if (!window.confirm(`Permanently delete ${u.email}?\n\nThis wipes their books, reviews, wishlist, lists, payments, and email log. It cannot be undone.`)) return;
    try {
      await api.delete(`/admin/users/${u.id}`);
      setSignups((rows) => rows.filter((r) => r.id !== u.id));
      toast.success(`Deleted ${u.email}`);
    } catch (e) {
      toast.error(formatApiError(e));
    }
  };

  useEffect(() => {
    if (!user || typeof user !== "object") return;
    load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [user]);

  if (loading) return <div className="p-12 font-mono uppercase tracking-widest text-xs text-[#8C7F65]">Loading…</div>;
  if (!user || typeof user !== "object") return <Navigate to="/login" replace />;
  if (error) return <div className="p-12 max-w-3xl mx-auto"><div className="font-heading text-2xl text-[#7C1D1D]">Forbidden</div><p className="mt-3 text-[#4A4030]">{error}</p></div>;
  if (!stats) return <div className="p-12 font-mono uppercase tracking-widest text-xs text-[#8C7F65]">Loading stats…</div>;

  return (
    <div className="max-w-7xl mx-auto px-6 md:px-12 py-12 fade-up" data-testid="admin-page">
      <div className="flex items-start justify-between gap-4 flex-wrap mb-10">
        <div>
          <div className="font-mono uppercase tracking-[0.25em] text-xs text-[#4A4030]">Internal</div>
          <h1 className="font-heading text-4xl md:text-5xl text-[#0A0A0A] mt-3 tracking-tight">Admin dashboard</h1>
          {lastRefreshedAt && (
            <div className="font-mono uppercase tracking-widest text-[10px] text-[#525252] mt-2" data-testid="admin-last-refresh">
              Last refreshed {lastRefreshedAt.toLocaleTimeString()}
            </div>
          )}
        </div>
        <button
          type="button"
          onClick={load}
          disabled={refreshing}
          className="inline-flex items-center gap-2 border border-[rgba(10,10,10,0.18)] bg-[#FAF5E6] hover:bg-black/5 text-[#0A0A0A] font-mono uppercase tracking-widest text-[10px] px-4 py-2.5 rounded-sm transition-colors disabled:opacity-50"
          data-testid="admin-refresh-btn"
        >
          <svg className={`w-3 h-3 ${refreshing ? "animate-spin" : ""}`} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M3 12a9 9 0 0 1 15-6.7L21 8"/><path d="M21 3v5h-5"/><path d="M21 12a9 9 0 0 1-15 6.7L3 16"/><path d="M3 21v-5h5"/></svg>
          {refreshing ? "Refreshing…" : "Refresh"}
        </button>
      </div>

      <div className="grid grid-cols-2 md:grid-cols-4 gap-px bg-[rgba(10,10,10,0.10)] border border-[rgba(10,10,10,0.10)]">
        <Metric icon={<Users className="w-5 h-5" strokeWidth={1.5} />} label="Total users" value={stats.users.total} testId="metric-users" />
        <Metric icon={<BadgeCheck className="w-5 h-5" strokeWidth={1.5} />} label="Reads+ subscribers" value={stats.users.plus} testId="metric-plus" />
        <Metric icon={<BookOpen className="w-5 h-5" strokeWidth={1.5} />} label="Books added" value={stats.books} testId="metric-books" />
        <Metric icon={<MessageSquare className="w-5 h-5" strokeWidth={1.5} />} label="Reviews" value={stats.reviews} testId="metric-reviews" />
        <Metric icon={<Mail className="w-5 h-5" strokeWidth={1.5} />} label="Mailing list" value={stats.mailing_list_count} testId="metric-mailing" />
        <Metric icon={<Euro className="w-5 h-5" strokeWidth={1.5} />} label="Revenue (EUR)" value={`€${stats.payments.revenue_eur}`} testId="metric-revenue" />
        <Metric icon={<Euro className="w-5 h-5" strokeWidth={1.5} />} label="Completed payments" value={stats.payments.completed} testId="metric-paid" />
        <Metric icon={<Users className="w-5 h-5" strokeWidth={1.5} />} label="Authors reviewed" value={stats.authors_reviewed} testId="metric-authors" />
      </div>

      {/* Extended breakdown: reader engagement, email health, codebase, mongo */}
      {stats.engagement && (
        <section className="mt-14">
          <h2 className="font-heading text-2xl text-[#0A0A0A] tracking-tight mb-4">Engagement</h2>
          <div className="grid grid-cols-2 md:grid-cols-4 gap-px bg-[rgba(10,10,10,0.10)] border border-[rgba(10,10,10,0.10)]" data-testid="engagement-block">
            <Metric icon={<Users className="w-5 h-5" strokeWidth={1.5} />} label="Active readers (7d)" value={stats.engagement.active_readers_7d} testId="metric-active-7d" />
            <Metric icon={<BadgeCheck className="w-5 h-5" strokeWidth={1.5} />} label="Verified accounts" value={stats.users.verified ?? 0} testId="metric-verified" />
            <Metric icon={<ShieldCheck className="w-5 h-5" strokeWidth={1.5} />} label="Under-14 awaiting" value={stats.users.under_14_awaiting_consent ?? 0} testId="metric-u14" />
            <Metric icon={<BadgeCheck className="w-5 h-5" strokeWidth={1.5} />} label="Lifetime Plus" value={stats.users.lifetime_plus ?? 0} testId="metric-lifetime" />
          </div>
        </section>
      )}

      {stats.email && (
        <section className="mt-14">
          <h2 className="font-heading text-2xl text-[#0A0A0A] tracking-tight mb-4">Email health</h2>
          <div className="grid grid-cols-2 md:grid-cols-4 gap-px bg-[rgba(10,10,10,0.10)] border border-[rgba(10,10,10,0.10)]" data-testid="email-block">
            <Metric icon={<Mail className="w-5 h-5" strokeWidth={1.5} />} label="Total sends" value={stats.email.sends_total} testId="metric-email-total" />
            <Metric icon={<Mail className="w-5 h-5" strokeWidth={1.5} />} label="Sandbox-blocked" value={stats.email.sends_blocked_sandbox} testId="metric-email-blocked" />
          </div>
          {stats.email.sends_blocked_sandbox > 0 && (
            <p className="text-xs text-[#8C7F65] mt-2 font-mono uppercase tracking-widest">
              Blocked sends indicate the pre-verified-domain (sandbox) phase. Should stay at zero now.
            </p>
          )}
        </section>
      )}

      {stats.codebase && (
        <section className="mt-14">
          <h2 className="font-heading text-2xl text-[#0A0A0A] tracking-tight mb-4">Codebase</h2>
          <div className="grid grid-cols-2 md:grid-cols-4 gap-px bg-[rgba(10,10,10,0.10)] border border-[rgba(10,10,10,0.10)]" data-testid="codebase-block">
            <Metric icon={<Code className="w-5 h-5" strokeWidth={1.5} />} label="Total lines of code" value={stats.codebase.total_loc?.toLocaleString?.() ?? stats.codebase.total_loc} testId="metric-loc-total" />
            <Metric icon={<Code className="w-5 h-5" strokeWidth={1.5} />} label="Backend (Python)" value={stats.codebase.backend_python_loc?.toLocaleString?.()} testId="metric-loc-backend" />
            <Metric icon={<Code className="w-5 h-5" strokeWidth={1.5} />} label="Frontend (JS/CSS)" value={stats.codebase.frontend_js_loc?.toLocaleString?.()} testId="metric-loc-frontend" />
            <Metric icon={<TestTube2 className="w-5 h-5" strokeWidth={1.5} />} label="Backend tests" value={`${stats.codebase.backend_test_cases} in ${stats.codebase.backend_test_files} files`} testId="metric-tests" />
          </div>
        </section>
      )}

      {stats.mongo && (
        <section className="mt-14" data-testid="mongo-block">
          <h2 className="font-heading text-2xl text-[#0A0A0A] tracking-tight mb-4">MongoDB</h2>
          <div className="grid grid-cols-2 md:grid-cols-4 gap-px bg-[rgba(10,10,10,0.10)] border border-[rgba(10,10,10,0.10)] mb-6">
            <Metric icon={<Database className="w-5 h-5" strokeWidth={1.5} />} label="Collections" value={stats.mongo.db_stats?.collections ?? "—"} testId="metric-mongo-collections" />
            <Metric icon={<Database className="w-5 h-5" strokeWidth={1.5} />} label="Total documents" value={(stats.mongo.db_stats?.objects ?? 0).toLocaleString()} testId="metric-mongo-objects" />
            <Metric icon={<HardDrive className="w-5 h-5" strokeWidth={1.5} />} label="Data size" value={_fmtBytes(stats.mongo.db_stats?.data_size_bytes)} testId="metric-mongo-data" />
            <Metric icon={<HardDrive className="w-5 h-5" strokeWidth={1.5} />} label="Index size" value={_fmtBytes(stats.mongo.db_stats?.index_size_bytes)} testId="metric-mongo-index" />
          </div>
          <div className="border border-[rgba(10,10,10,0.10)]">
            <table className="w-full text-sm">
              <thead className="bg-[#FAF5E6] border-b border-[rgba(10,10,10,0.10)]">
                <tr><Th>Collection</Th><Th className="text-right">Documents</Th></tr>
              </thead>
              <tbody>
                {(stats.mongo.collections || []).map((c) => (
                  <tr key={c.name} className="border-b border-[rgba(10,10,10,0.06)]" data-testid={`mongo-col-${c.name}`}>
                    <Td className="font-mono text-xs">{c.name}</Td>
                    <Td className="font-mono text-xs text-right">{c.count.toLocaleString()}</Td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </section>
      )}

      {/* All members */}
      <section className="mt-16">
        <div className="flex items-end justify-between mb-6 gap-4 flex-wrap">
          <div>
            <h2 className="font-heading text-2xl text-[#0A0A0A] tracking-tight">All members</h2>
            <p className="font-mono uppercase tracking-widest text-[10px] text-[#525252] mt-1" data-testid="admin-signups-count">
              {signups.length} total · IP + browser captured at signup · red row = still unverified
            </p>
          </div>
          <EmailActions
            emails={signups.map((u) => u.email).filter(Boolean)}
            rows={signups.map((u) => ({ email: u.email, name: u.name, role: u.role, verified: u.verified, signup_ip: u.signup_ip, signup_user_agent: u.signup_user_agent, joined: u.created_at }))}
            filename="reads-signups"
            testIdPrefix="signups"
          />
        </div>
        <div className="border border-[rgba(10,10,10,0.10)] overflow-x-auto max-h-[600px] overflow-y-auto">
          <table className="w-full text-sm">
            <thead className="bg-[#FAF5E6] border-b border-[rgba(10,10,10,0.10)] sticky top-0 z-10">
              <tr>
                <Th>Name</Th><Th>Email</Th><Th>Status</Th><Th>Plan</Th><Th>IP</Th><Th>Browser</Th><Th>Joined</Th><Th>Actions</Th>
              </tr>
            </thead>
            <tbody data-testid="recent-signups-tbody">
              {signups.map((u) => {
                const isPlusRow = u.subscription_status === "plus" || Boolean(u.is_plus);
                return (
                <tr
                  key={u.id}
                  className={`border-b border-[rgba(10,10,10,0.06)] ${u.verified ? "" : "bg-[#B91C1C]/5"}`}
                  data-testid={`signup-${u.id}`}
                >
                  <Td>{u.name}</Td>
                  <Td className="font-mono text-xs">{u.email}</Td>
                  <Td>
                    {u.verified
                      ? <span className="inline-flex items-center gap-1 font-mono uppercase tracking-widest text-[10px] text-[#166534]" data-testid={`signup-status-${u.id}`}>✓ verified</span>
                      : <span className="inline-flex items-center gap-1 font-mono uppercase tracking-widest text-[10px] text-[#B91C1C]" data-testid={`signup-status-${u.id}`}>pending</span>}
                  </Td>
                  <Td>
                    {isPlusRow
                      ? <span className="inline-flex items-center gap-1 font-mono uppercase tracking-widest text-[10px] text-[#8B7100] bg-[#F5E9B8] px-1.5 py-0.5" data-testid={`signup-plan-${u.id}`}>★ Reads+</span>
                      : <span className="font-mono uppercase tracking-widest text-[10px] text-[#525252]" data-testid={`signup-plan-${u.id}`}>free</span>}
                  </Td>
                  <Td className="font-mono text-xs text-[#4A4030]">{u.signup_ip || "—"}</Td>
                  <Td className="font-mono text-xs text-[#4A4030]" title={u.signup_user_agent || ""}>{summariseUA(u.signup_user_agent)}</Td>
                  <Td className="font-mono text-xs text-[#4A4030]">{u.created_at ? new Date(u.created_at).toLocaleString(undefined, { dateStyle: "medium", timeStyle: "short" }) : "—"}</Td>
                  <Td>
                    <div className="flex items-center gap-2">
                      <button
                        type="button"
                        onClick={() => togglePlus(u)}
                        className={`inline-flex items-center gap-1 font-mono uppercase tracking-widest text-[10px] px-2 py-1 rounded-sm transition-colors ${isPlusRow ? "border border-[#B91C1C]/40 text-[#B91C1C] hover:bg-[#B91C1C]/5" : "border border-[#8B7100]/50 text-[#8B7100] hover:bg-[#F5E9B8]"}`}
                        data-testid={`toggle-plus-${u.id}`}
                      >
                        {isPlusRow ? <><XCircle className="w-3 h-3" strokeWidth={1.5}/> Revoke</> : <><Star className="w-3 h-3" strokeWidth={1.5}/> Grant Reads+</>}
                      </button>
                      <button
                        type="button"
                        onClick={() => deleteUser(u)}
                        className="inline-flex items-center gap-1 border border-[#B91C1C]/40 text-[#B91C1C] hover:bg-[#B91C1C]/10 font-mono uppercase tracking-widest text-[10px] px-2 py-1 rounded-sm transition-colors"
                        data-testid={`delete-user-${u.id}`}
                        title="Permanently delete user + all their data"
                      >
                        <Trash2 className="w-3 h-3" strokeWidth={1.5}/> Delete
                      </button>
                    </div>
                  </Td>
                </tr>
                );
              })}
              {signups.length === 0 && <tr><Td colSpan={8} className="text-[#8C7F65] text-center py-8">No members yet.</Td></tr>}
            </tbody>
          </table>
        </div>
      </section>

      {/* Activity feed — reverse-chronological, all recent events across the app */}
      <section className="mt-16">
        <div className="flex items-end justify-between mb-6 gap-4 flex-wrap">
          <div>
            <h2 className="font-heading text-2xl text-[#0A0A0A] tracking-tight inline-flex items-center gap-2">
              <Activity className="w-4 h-4" strokeWidth={1.5} /> Activity feed
            </h2>
            <p className="font-mono uppercase tracking-widest text-[10px] text-[#525252] mt-1">
              Last {activity.length} events across signups, book adds, reviews, Reads+ payments & cancellations
            </p>
          </div>
        </div>
        <div className="border border-[rgba(10,10,10,0.10)] max-h-[500px] overflow-y-auto">
          <ul data-testid="admin-activity-feed">
            {activity.map((e, i) => (
              <li key={i} className="flex items-start gap-4 px-4 py-3 border-b border-[rgba(10,10,10,0.06)] hover:bg-[#FAF5E6]" data-testid={`activity-${i}`}>
                <div className="pt-1 shrink-0">
                  {e.type === "signup" && <UserPlus className="w-4 h-4 text-[#166534]" strokeWidth={1.5} />}
                  {e.type === "book_added" && <BookOpen className="w-4 h-4 text-[#0A0A0A]" strokeWidth={1.5} />}
                  {e.type === "review" && <Star className="w-4 h-4 text-[#8B7100]" strokeWidth={1.5} />}
                  {e.type === "plus_upgrade" && <Sparkles className="w-4 h-4 text-[#8B7100]" strokeWidth={1.5} />}
                  {e.type === "plus_cancel" && <XCircle className="w-4 h-4 text-[#B91C1C]" strokeWidth={1.5} />}
                </div>
                <div className="min-w-0 flex-1">
                  <div className="text-sm text-[#0A0A0A]">{e.title}</div>
                  <div className="font-mono uppercase tracking-widest text-[9px] text-[#525252] mt-1 truncate">
                    {e.user_email || "—"} · {e.at ? new Date(e.at).toLocaleString(undefined, { dateStyle: "medium", timeStyle: "short" }) : "—"}
                  </div>
                </div>
              </li>
            ))}
            {activity.length === 0 && <li className="text-[#8C7F65] text-center py-8 font-mono uppercase tracking-widest text-[10px]">No recent activity.</li>}
          </ul>
        </div>
      </section>

      {/* Mailing list */}
      <section className="mt-16">
        <div className="flex items-end justify-between mb-6 gap-4 flex-wrap">
          <h2 className="font-heading text-2xl text-[#0A0A0A] tracking-tight">Mailing list signups</h2>
          <EmailActions
            emails={mailing.map((m) => m.email).filter(Boolean)}
            rows={mailing.map((m) => ({ email: m.email, forwarded_to: m.forwarded_to, date: m.created_at }))}
            filename="reads-mailing-list"
            testIdPrefix="mailing"
          />
        </div>
        <div className="border border-[rgba(10,10,10,0.10)]">
          <table className="w-full text-sm">
            <thead className="bg-[#FAF5E6] border-b border-[rgba(10,10,10,0.10)]">
              <tr><Th>Email</Th><Th>Forwarded to</Th><Th>Date</Th></tr>
            </thead>
            <tbody>
              {mailing.map((m) => (
                <tr key={m.id} className="border-b border-[rgba(10,10,10,0.06)]" data-testid={`mail-${m.id}`}>
                  <Td>{m.email}</Td>
                  <Td className="font-mono text-xs">{m.forwarded_to}</Td>
                  <Td className="font-mono text-xs text-[#4A4030]">{new Date(m.created_at).toLocaleDateString()}</Td>
                </tr>
              ))}
              {mailing.length === 0 && <tr><Td colSpan={3} className="text-[#8C7F65] text-center py-8">No signups yet.</Td></tr>}
            </tbody>
          </table>
        </div>
      </section>

      {/* Pending password resets (manual delivery while email is in sandbox) */}
      <section className="mt-16" data-testid="admin-password-resets">
        <div className="flex items-end justify-between mb-2 gap-4 flex-wrap">
          <h2 className="font-heading text-2xl text-[#0A0A0A] tracking-tight inline-flex items-center gap-2">
            <KeyRound className="w-5 h-5" strokeWidth={1.5} /> Pending password resets
          </h2>
          <span className="font-mono uppercase tracking-widest text-[10px] text-[#4A4030]">
            {resets.length} active
          </span>
        </div>
        <p className="text-[#4A4030] text-sm mb-6 max-w-3xl">
          Until the Resend domain is verified, reset emails to non-owner inboxes are blocked.
          Until then, copy the link below and send it to the user directly via your messages or a private channel.
          Each link is single-use and expires after 24 hours.
        </p>
        <div className="border border-[rgba(10,10,10,0.10)]">
          <table className="w-full text-sm">
            <thead className="bg-[#FAF5E6] border-b border-[rgba(10,10,10,0.10)]">
              <tr><Th>User</Th><Th>Reset link</Th><Th>Expires</Th><Th>Copy</Th></tr>
            </thead>
            <tbody>
              {resets.map((r) => (
                <tr key={r.email} className="border-b border-[rgba(10,10,10,0.06)]" data-testid={`reset-${r.email}`}>
                  <Td>
                    <div className="text-[#0A0A0A]">{r.name || "—"}</div>
                    <div className="font-mono text-xs text-[#4A4030]">{r.email}</div>
                  </Td>
                  <Td className="font-mono text-xs text-[#4A4030] break-all max-w-xs">{r.reset_url}</Td>
                  <Td className="font-mono text-xs text-[#4A4030]">{new Date(r.expires_at).toLocaleString()}</Td>
                  <Td>
                    <button
                      onClick={async () => {
                        try {
                          await navigator.clipboard.writeText(r.reset_url);
                          toast.success(`Copied reset link for ${r.email}`);
                        } catch {
                          toast.error("Could not copy");
                        }
                      }}
                      className="inline-flex items-center gap-1.5 border border-[rgba(10,10,10,0.18)] bg-[#FAF5E6] hover:bg-black/5 text-[#0A0A0A] font-mono uppercase tracking-widest text-[10px] px-2.5 py-1.5 rounded-sm transition-colors"
                      data-testid={`reset-copy-${r.email}`}
                    >
                      <Copy className="w-3 h-3" strokeWidth={1.5} /> Copy
                    </button>
                  </Td>
                </tr>
              ))}
              {resets.length === 0 && <tr><Td colSpan={4} className="text-[#8C7F65] text-center py-8">No pending reset links right now.</Td></tr>}
            </tbody>
          </table>
        </div>
      </section>

      {/* Pending email verifications (manual delivery while email is in sandbox) */}
      <section className="mt-16" data-testid="admin-email-verifications">
        <div className="flex items-end justify-between mb-2 gap-4 flex-wrap">
          <h2 className="font-heading text-2xl text-[#0A0A0A] tracking-tight inline-flex items-center gap-2">
            <ShieldCheck className="w-5 h-5" strokeWidth={1.5} /> Pending email verifications
          </h2>
          <span className="font-mono uppercase tracking-widest text-[10px] text-[#4A4030]">
            {verifications.length} pending
          </span>
        </div>
        <p className="text-[#4A4030] text-sm mb-6 max-w-3xl">
          New users can already log in and use Reads — verification is optional and just confirms ownership of an email address.
          Until the Resend domain is verified, the verification emails for non-owner addresses are silently dropped, so the
          link is shown here. Drop it into a private message or DM to the user and they'll be marked verified the moment they click.
        </p>
        <div className="border border-[rgba(10,10,10,0.10)]">
          <table className="w-full text-sm">
            <thead className="bg-[#FAF5E6] border-b border-[rgba(10,10,10,0.10)]">
              <tr><Th>User</Th><Th>Verify link</Th><Th>Signed up</Th><Th>Copy</Th></tr>
            </thead>
            <tbody>
              {verifications.map((v) => (
                <tr key={v.email} className="border-b border-[rgba(10,10,10,0.06)]" data-testid={`verify-${v.email}`}>
                  <Td>
                    <div className="text-[#0A0A0A]">{v.name || "—"}</div>
                    <div className="font-mono text-xs text-[#4A4030]">{v.email}</div>
                  </Td>
                  <Td className="font-mono text-xs text-[#4A4030] break-all max-w-xs">{v.verify_url}</Td>
                  <Td className="font-mono text-xs text-[#4A4030]">{v.signed_up_at ? new Date(v.signed_up_at).toLocaleDateString() : "—"}</Td>
                  <Td>
                    <button
                      onClick={async () => {
                        try {
                          await navigator.clipboard.writeText(v.verify_url);
                          toast.success(`Copied verify link for ${v.email}`);
                        } catch {
                          toast.error("Could not copy");
                        }
                      }}
                      className="inline-flex items-center gap-1.5 border border-[rgba(10,10,10,0.18)] bg-[#FAF5E6] hover:bg-black/5 text-[#0A0A0A] font-mono uppercase tracking-widest text-[10px] px-2.5 py-1.5 rounded-sm transition-colors"
                      data-testid={`verify-copy-${v.email}`}
                    >
                      <Copy className="w-3 h-3" strokeWidth={1.5} /> Copy
                    </button>
                  </Td>
                </tr>
              ))}
              {verifications.length === 0 && <tr><Td colSpan={4} className="text-[#8C7F65] text-center py-8">Everyone&apos;s verified — nice.</Td></tr>}
            </tbody>
          </table>
        </div>
      </section>
    </div>
  );
}

function Metric({ icon, label, value, testId }) {
  return (
    <div className="bg-[#FAF5E6] p-6">
      <div className="text-[#0A0A0A] mb-2">{icon}</div>
      <div className="font-mono uppercase tracking-widest text-[10px] text-[#4A4030]">{label}</div>
      <div className="font-heading text-3xl text-[#0A0A0A] mt-1 tabular-nums" data-testid={testId}>{value}</div>
    </div>
  );
}

function Th({ children }) { return <th className="text-left font-mono uppercase tracking-widest text-[10px] text-[#4A4030] px-4 py-3">{children}</th>; }
function Td({ children, colSpan, className = "" }) { return <td colSpan={colSpan} className={`px-4 py-3 text-[#0A0A0A] ${className}`}>{children}</td>; }

// Turn a raw User-Agent string into "Chrome / Windows" style. Deliberately
// shallow — anything beyond top-4 browsers × top-3 OSes lands in "Other".
function summariseUA(ua) {
  if (!ua) return "—";
  const s = ua.toLowerCase();
  let browser = "Other";
  if (s.includes("edg/")) browser = "Edge";
  else if (s.includes("firefox")) browser = "Firefox";
  else if (s.includes("chrome") && !s.includes("chromium")) browser = "Chrome";
  else if (s.includes("safari") && !s.includes("chrome")) browser = "Safari";
  else if (s.includes("curl")) browser = "curl";
  else if (s.includes("bot") || s.includes("spider") || s.includes("crawler")) browser = "Bot";
  let os = "";
  if (s.includes("windows")) os = "Windows";
  else if (s.includes("mac os") || s.includes("macintosh")) os = "macOS";
  else if (s.includes("iphone") || s.includes("ipad")) os = "iOS";
  else if (s.includes("android")) os = "Android";
  else if (s.includes("linux")) os = "Linux";
  return os ? `${browser} / ${os}` : browser;
}

// Format a byte count into a human-readable string. Used by the MongoDB
// stats section. Returns "—" when the value is undefined so the tile doesn't
// crash the render.
function _fmtBytes(bytes) {
  if (bytes == null || Number.isNaN(bytes)) return "—";
  const units = ["B", "KB", "MB", "GB", "TB"];
  let i = 0;
  let n = Number(bytes);
  while (n >= 1024 && i < units.length - 1) { n /= 1024; i++; }
  return `${n.toFixed(n >= 10 || i === 0 ? 0 : 1)} ${units[i]}`;
}

function EmailActions({ emails, rows, filename, testIdPrefix }) {
  const count = emails.length;

  const copyEmails = async () => {
    if (count === 0) return;
    const joined = emails.join(", ");
    try {
      await navigator.clipboard.writeText(joined);
      toast.success(`Copied ${count} ${count === 1 ? "email" : "emails"} to clipboard`);
    } catch {
      // Older browsers / non-secure context fallback
      const ta = document.createElement("textarea");
      ta.value = joined;
      document.body.appendChild(ta);
      ta.select();
      document.execCommand("copy");
      document.body.removeChild(ta);
      toast.success(`Copied ${count} ${count === 1 ? "email" : "emails"} to clipboard`);
    }
  };

  const downloadCsv = () => {
    if (rows.length === 0) return;
    const headers = Object.keys(rows[0]);
    const escape = (v) => {
      const s = v == null ? "" : String(v);
      return /[",\n]/.test(s) ? `"${s.replace(/"/g, '""')}"` : s;
    };
    const csv = [
      headers.join(","),
      ...rows.map((r) => headers.map((h) => escape(r[h])).join(",")),
    ].join("\n");
    const blob = new Blob([csv], { type: "text/csv;charset=utf-8" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    const stamp = new Date().toISOString().slice(0, 10);
    a.href = url;
    a.download = `${filename}-${stamp}.csv`;
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    URL.revokeObjectURL(url);
    toast.success(`Downloaded ${rows.length} ${rows.length === 1 ? "row" : "rows"}`);
  };

  return (
    <div className="flex items-center gap-2">
      <span className="font-mono uppercase tracking-widest text-[10px] text-[#4A4030]" data-testid={`${testIdPrefix}-count`}>
        {count} {count === 1 ? "email" : "emails"}
      </span>
      <button
        onClick={copyEmails}
        disabled={count === 0}
        className="inline-flex items-center gap-1.5 border border-[rgba(10,10,10,0.18)] bg-[#FAF5E6] hover:bg-black/5 text-[#0A0A0A] font-mono uppercase tracking-widest text-[10px] px-3 py-1.5 rounded-sm transition-colors disabled:opacity-40"
        data-testid={`${testIdPrefix}-copy-emails`}
      >
        <Copy className="w-3 h-3" strokeWidth={1.5} /> Copy emails
      </button>
      <button
        onClick={downloadCsv}
        disabled={rows.length === 0}
        className="inline-flex items-center gap-1.5 bg-[#0A0A0A] hover:bg-[#1F1F1F] text-[#F2EAD3] font-mono uppercase tracking-widest text-[10px] px-3 py-1.5 rounded-sm transition-colors disabled:opacity-40"
        data-testid={`${testIdPrefix}-download-csv`}
      >
        <Download className="w-3 h-3" strokeWidth={1.5} /> Download CSV
      </button>
    </div>
  );
}
